import { useEffect, useRef, useState } from "react";
import { apiPost, tryApiGet } from "../api/client";
import { useAppData } from "../state/AppData";
import { navigate } from "../lib/router";
import { LIVE_WINDOW_ORDER, type LiveWindow } from '../lib/definitions/analysis';
import { colorFor, STATUS } from '../lib/definitions/palette';
import { isConnectedRadioTrustTier } from '../lib/definitions/trust';
import { formatNumber, relativeTime, truncateText } from "../lib/format";
import { ObserverBadge } from "./ObserverBadge";
import type { AskCitation, AskCitationRef, AskConfig, AskHistoryItem, AskHistoryResponse, AskResponse } from "../api/types";

// Phase 4's Ask chat — scoping phase 4 as "audit detectors, coverage-gap
// and observer-disagreement views, and the Ask chat" left the chat half
// unbuilt on both sides. The requirement: asking about mesh stats should
// draw on not just what the radio sees but what CoreScope or the mesh
// view sees. That is the whole point of this panel, not
// a nicety — every answer's data scope is shown prominently, never a
// footnote, and is visibly different (colour, not just text) depending on
// whether it drew on external observers or only our own radio.
//
// Placement (App.tsx's Shell): a global floating panel toggled from a
// header button, not a new nav destination (pages should not appear
// without discussion) and not embedded in any one
// existing page. "Ask about the mesh" is cross-cutting — its own
// grounded_on list spans nodes/packets/telemetry/audit/coverage at once —
// so it doesn't naturally belong to any single destination, and the
// side-panes that already exist on Live/Messages are narrow and busy.
// Reachable from every route the same way SelectionDrawer already is.

interface Exchange {
  id: string;
  question: string;
  response: AskResponse | null;
  error: string | null;
}

function goToCitationRef(ref: AskCitationRef): void {
  if (!ref) return;
  if ("native_id" in ref) {
    navigate(`#/nodes/${encodeURIComponent(ref.native_id)}`);
  } else if ("detector" in ref) {
    navigate(`#/analysis?detector=${encodeURIComponent(ref.detector)}`);
  }
}

// Citations bury the answer at real volume — found against the live
// backend, 2026-09-20: a question about prefix collisions came back with
// 18 citations, each a full multi-sentence finding summary (the backend
// was putting the whole thing into `label`, since fixed at the source to
// send a short identifier instead — but built defensively here regardless,
// since a future citation kind isn't guaranteed to stay short just because
// this one now is). Two fixes, independent of each other: (1) every label
// is hard-capped (CITATION_LABEL_MAX) no matter what the backend sends,
// full text kept in the title tooltip; (2) citations that share the same
// `ref` — the common real-world case being N findings from the same
// detector, since a detector ref only identifies the detector, not a
// specific finding — collapse into ONE pill with a count, since they all
// navigate to the identical destination anyway.
const CITATION_LABEL_MAX = 40;

function citationGroupKey(c: AskCitation): string {
  if (!c.ref) return `none:${c.kind}:${c.label}`;
  if ("native_id" in c.ref) return `node:${c.ref.protocol}:${c.ref.native_id}`;
  if ("detector" in c.ref) return `detector:${c.ref.detector}`;
  return `unknown:${c.kind}:${c.label}`;
}

interface CitationGroup {
  key: string;
  label: string;
  ref: AskCitationRef;
  count: number;
}

function groupCitations(citations: AskCitation[]): CitationGroup[] {
  const map = new Map<string, CitationGroup>();
  for (const c of citations) {
    const key = citationGroupKey(c);
    const existing = map.get(key);
    if (existing) existing.count += 1;
    else map.set(key, { key, label: c.label, ref: c.ref, count: 1 });
  }
  return Array.from(map.values());
}

// True when this answer drew on at least one observer that isn't our own
// first-party radio — the exact distinction this requirement is about.
// Uses TrustTierInfo's own `ownRadio` field (lib/definitions.ts) rather
// than a hardcoded tier list — found and fixed 2026-09-20 while building
// the connected-radio/firehose provenance filter: the old hardcoded
// `=== "external_unauthenticated" || === "relayed_mqtt"` check silently
// mis-scored "unverified" (a real, live tier — our own vnode's traffic
// whose reception path couldn't be confirmed) as first-party-only,
// showing a false "First-party radio only" banner. A missing/unrecognized
// trust_tier now correctly counts as external too (isConnectedRadioTrustTier
// falls back to "unknown", ownRadio: false) rather than the reverse.
function hasExternalObserver(observers: string[], observerTrust: Map<string, string>): boolean {
  return observers.some((oid) => !isConnectedRadioTrustTier(observerTrust.get(oid)));
}

function DataScopeBanner({ scope, windowKey, observerTrust }: { scope: AskResponse["data_scope"]; windowKey: string; observerTrust: Map<string, string> }) {
  const external = hasExternalObserver(scope.observers, observerTrust);
  const color = external ? STATUS.good : STATUS.warning;
  return (
    <div className="ask-scope" style={{ borderColor: colorFor(color, "dark") }}>
      <div className="ask-scope-label" style={{ color: colorFor(color, "dark") }}>
        {external
          ? "Includes external observer data — not just our own radio."
          : "First-party radio only — no external observer (CoreScope, meshview) data included in this answer."}
      </div>
      <div className="ask-scope-observers">
        {scope.observers.map((oid) => (
          <ObserverBadge key={oid} observerId={oid} trustTier={observerTrust.get(oid)} />
        ))}
      </div>
      <div className="ask-scope-meta">
        Window: {windowKey} · {formatNumber(scope.node_count)} node(s) · {formatNumber(scope.packet_count)} packet(s)
      </div>
    </div>
  );
}

function AnswerBlock({ response, windowKey, observerTrust }: { response: AskResponse; windowKey: string; observerTrust: Map<string, string> }) {
  return (
    <>
      <div className="ask-answer">{response.answer}</div>
      <div className="ask-meta">
        {response.model} ({response.tier}) · {formatNumber(response.elapsed_ms)}ms
      </div>
      <DataScopeBanner scope={response.data_scope} windowKey={windowKey} observerTrust={observerTrust} />
      {response.citations.length > 0 && (
        <div className="ask-citations">
          {groupCitations(response.citations).map((g) => {
            const shown = truncateText(g.label, CITATION_LABEL_MAX) + (g.count > 1 ? ` (${g.count})` : "");
            return g.ref ? (
              <button key={g.key} type="button" className="ask-citation" title={g.label} onClick={() => goToCitationRef(g.ref)}>
                {shown}
              </button>
            ) : (
              <span key={g.key} className="ask-citation ask-citation-inert" title={g.label}>
                {shown}
              </span>
            );
          })}
        </div>
      )}
    </>
  );
}

export function AskPanel({ onClose }: { onClose: () => void }) {
  const { observerTrust } = useAppData();
  const [config, setConfig] = useState<AskConfig | null | "unavailable">(null);
  const [history, setHistory] = useState<AskHistoryItem[] | null | "unavailable">(null);
  const [windowKey, setWindowKey] = useState<LiveWindow>("24h");
  const [question, setQuestion] = useState("");
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [sending, setSending] = useState(false);
  const lastExchangeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    tryApiGet<AskConfig>("/api/ask/config")
      .then((r) => !cancelled && setConfig(r ?? "unavailable"))
      .catch(() => !cancelled && setConfig("unavailable"));
    tryApiGet<AskHistoryResponse>("/api/ask/history?limit=20")
      .then((r) => !cancelled && setHistory(r?.items ?? "unavailable"))
      .catch(() => !cancelled && setHistory("unavailable"));
    return () => {
      cancelled = true;
    };
  }, []);

  // Scroll the LATEST exchange's own top into view, not the container's
  // absolute bottom (found in this same round: scrolling to `scrollHeight`
  // meant a long citation list at the tail of the newest answer pushed
  // that answer itself above the visible area the instant it arrived —
  // "the answer must always be visible and above the citations" is a
  // structural requirement, not just a matter of making citations shorter,
  // so this holds even if a future answer or its citations are long for
  // some other reason). Re-runs on every exchange update, not just a new
  // question, since the tall content actually appears when the response
  // (and its citations) fills in, not when the question is first asked.
  useEffect(() => {
    lastExchangeRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [exchanges]);

  async function onAsk(e: React.FormEvent) {
    e.preventDefault();
    const q = question.trim();
    if (!q || sending) return;
    const id = `local-${Date.now()}`;
    setExchanges((prev) => [...prev, { id, question: q, response: null, error: null }]);
    setQuestion("");
    setSending(true);
    try {
      const res = await apiPost<AskResponse>("/api/ask", { question: q, window: windowKey });
      setExchanges((prev) => prev.map((ex) => (ex.id === id ? { ...ex, response: res } : ex)));
    } catch (err) {
      setExchanges((prev) => prev.map((ex) => (ex.id === id ? { ...ex, error: err instanceof Error ? err.message : "Ask failed." } : ex)));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="ask-panel">
      <div className="panel-header">
        <span>Ask</span>
        <button className="close-btn" onClick={onClose}>
          ✕
        </button>
      </div>

      {config === null && <div className="node-detail-note">Loading…</div>}
      {config === "unavailable" && <div className="node-detail-note">Ask isn't available from this backend right now.</div>}
      {config && config !== "unavailable" && !config.enabled && <div className="node-detail-note">Ask is disabled on this instance.</div>}

      {config && config !== "unavailable" && config.enabled && (
        <>
          <div className="ask-config-note">
            Model: {config.model} ({config.tier}) · grounded on {config.grounded_on.join(", ")}
          </div>

          <div className="ask-history-list">
            {history === null && <div className="node-detail-note">Loading history…</div>}
            {history === "unavailable" && exchanges.length === 0 && (
              <div className="node-detail-note">History isn't available from this backend right now.</div>
            )}
            {Array.isArray(history) && history.length > 0 && exchanges.length === 0 && (
              <>
                <div className="ask-history-label">Previous questions</div>
                {history.map((h) => (
                  <div key={h.id} className="ask-exchange">
                    <div className="ask-question">{h.question}</div>
                    <div className="ask-answer">{h.answer}</div>
                    <div className="ask-meta">
                      {h.model} · {relativeTime(h.asked_at)}
                    </div>
                  </div>
                ))}
              </>
            )}
            {Array.isArray(history) && history.length === 0 && exchanges.length === 0 && (
              <div className="empty-state">No questions asked yet.</div>
            )}

            {exchanges.map((ex, i) => (
              <div key={ex.id} className="ask-exchange" ref={i === exchanges.length - 1 ? lastExchangeRef : undefined}>
                <div className="ask-question">{ex.question}</div>
                {ex.response ? (
                  <AnswerBlock response={ex.response} windowKey={windowKey} observerTrust={observerTrust} />
                ) : ex.error ? (
                  <div className="auth-error">{ex.error}</div>
                ) : (
                  <div className="node-detail-note">Thinking…</div>
                )}
              </div>
            ))}
          </div>

          <form className="ask-composer" onSubmit={onAsk}>
            <div className="theme-toggle" role="group" aria-label="Ask window">
              {LIVE_WINDOW_ORDER.map((w) => (
                <button key={w} type="button" data-active={windowKey === w} onClick={() => setWindowKey(w)}>
                  {w}
                </button>
              ))}
            </div>
            <textarea rows={2} placeholder="Ask about the mesh…" value={question} onChange={(e) => setQuestion(e.target.value)} />
            <button type="submit" className="settings-save-btn" disabled={sending || question.trim().length === 0}>
              {sending ? "Asking…" : "Ask"}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
